import { z } from "zod";

export const agentBillingPlanSchema = z
  .enum(["starter", "pro"])
  .describe("AgentBillingPlan");

export type AgentBillingPlan = z.infer<typeof agentBillingPlanSchema>;

export const featureTypeSchema = z
  .enum([
    "credits",
    "shopifyThemeBuilder.publishedElements",
    "sites",
    "shopifyThemeBuilder.projects",
  ])
  .describe("FeatureType");

export type FeatureType = z.infer<typeof featureTypeSchema>;

export const creditBalanceTypeSchema = z
  .enum(["base", "purchase"])
  .describe("CreditBalanceType");

export type CreditBalanceType = z.infer<typeof creditBalanceTypeSchema>;

export const creditTransactionTypeSchema = z
  .enum(["base", "purchase", "track", "refund", "admin", "migration", "topup"])
  .describe("CreditTransactionType");

export type CreditTransactionType = z.infer<typeof creditTransactionTypeSchema>;

// #region Per-message tracking + workspace billing prefs (agent-ui)

export const trackInputSchema = z
  .object({
    workspaceId: z.uuid(),
    messageId: z.uuid(),
  })
  .describe("CreditTrackInput");

export type CreditTrackInput = z.infer<typeof trackInputSchema>;

export const trackResultSchema = z
  .object({
    alreadyBilled: z.boolean(),
    creditsDeducted: z.number().int().nonnegative(),
    newBalanceCredits: z.number().int(),
    topupTriggered: z.boolean(),
    topupFailed: z.boolean(),
  })
  .describe("CreditTrackResult");

export type CreditTrackResult = z.infer<typeof trackResultSchema>;

export const workspaceBillingPreferencesSchema = z
  .object({
    topupEnabledAt: z.coerce.date().nullable(),
    topupThreshold: z.number().int().positive().nullable(),
    topupRefillTarget: z.number().int().positive().nullable(),
    topupFailedAt: z.date().nullable(),
    monthlySpendLimit: z.number().int().positive().nullable(),
  })
  .describe("WorkspaceBillingPreferences");

export type WorkspaceBillingPreferences = z.infer<
  typeof workspaceBillingPreferencesSchema
>;

export const dailySpendPointSchema = z
  .object({
    day: z.string(), // YYYY-MM-DD UTC
    cents: z.number().int().nonnegative(),
  })
  .describe("DailySpendPoint");

export type DailySpendPoint = z.infer<typeof dailySpendPointSchema>;

// #endregion

export const creditBalanceIntervalSchema = z
  .enum(["month", "one_off"])
  .describe("CreditBalanceInterval");

export type CreditBalanceInterval = z.infer<typeof creditBalanceIntervalSchema>;

export const adminCreditBalanceIntervalByType = {
  base: "month",
  purchase: "one_off",
} satisfies Record<CreditBalanceType, CreditBalanceInterval>;

export const agentBillingSubscriptionIntervalSchema = z
  .enum(["month", "year"])
  .describe("AgentBillingSubscriptionInterval");

export type AgentBillingSubscriptionInterval = z.infer<
  typeof agentBillingSubscriptionIntervalSchema
>;

export const priceIntervalSchema = z
  .enum(["month", "year", "one_off"])
  .describe("PriceInterval");

export type PriceInterval = z.infer<typeof priceIntervalSchema>;

// #region Rate card types

export type Price = {
  interval: PriceInterval;
  /** Price in minimum currency units (e.g. 12900 = $129.00 USD) */
  amount: number;
  currency: string;
  priceId: string;
  productId?: string;
  isDefault?: boolean;
  isSuperuser?: boolean;
  isDeprecated?: boolean;
};

export type Feature = {
  featureType: FeatureType;
  units: number | null;
  minimumCapacity: number;
};

export type Product = {
  productType: string;
  featureType: FeatureType;
  displayName: string;
  units: number;
  isEligible: boolean;
  maxProductCount: number | null;
  currentProductCount: number;
  prices: Price[];
};

export type RateCard = {
  rank: number | null;
  plan: AgentBillingPlan | null;
  canUpgrade: boolean;
  title: string;
  prices: Price[];
  features: Feature[];
  addOnProducts: Product[];
};

// #endregion

// #region Subscription view

export const PLAN_PRODUCT_TYPES = new Set<string>(
  agentBillingPlanSchema.options,
);

export const subscriptionItemSchema = z
  .object({
    productType: z.string(),
    priceId: z.string(),
    quantity: z.number().int().positive(),
    interval: agentBillingSubscriptionIntervalSchema,
  })
  .describe("SubscriptionItem");

export type SubscriptionItem = z.infer<typeof subscriptionItemSchema>;

export const subscriptionViewSchema = z
  .object({
    subscriptionItems: z.array(subscriptionItemSchema),
  })
  .describe("SubscriptionView");

export type SubscriptionView = z.infer<typeof subscriptionViewSchema>;

// #endregion

export const stripeSubscriptionStatusSchema = z
  .enum([
    "active",
    "past_due",
    "canceled",
    "incomplete",
    "incomplete_expired",
    "trialing",
    "paused",
    "unpaid",
  ])
  .describe("StripeSubscriptionStatus");

export type StripeSubscriptionStatus = z.infer<
  typeof stripeSubscriptionStatusSchema
>;

// #region Subscription types

export type AgentSubscription = {
  id: string;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  /**
   * Mirror of the Stripe sub status. Narrowed to the "currently-live" set that
   * `SubscriptionService.findActive` returns (active | trialing | past_due) so
   * UI can branch on trial state without an extra round-trip to Stripe.
   */
  status: StripeSubscriptionStatus;
  startedAt: Date;
  cancelAt: Date | null;
  currentPeriodEnd: Date | null;
  interval: AgentBillingSubscriptionInterval;
  plan: AgentBillingPlan;
  /** Total subscription amount in minimum currency units per billing interval */
  amount: number;
  currency: string;
  /** True when any item sits on an off-catalog contract price; UI shows "Custom" and locks self-serve changes. (yuxin, 2026-07-14, REPL-29329) */
  hasCustomPricing: boolean;
  items: {
    id: string;
    stripePriceId: string;
    stripeProductId: string;
    /** Price in minimum currency units (e.g. 12900 = $129.00 USD) */
    unitAmount: number;
    quantity: number;
    productType: string;
    currency: string;
  }[];
};

// #endregion
